/* global Promise*/

import _ from "lodash";

import ApiClient from "../../components/api-client";

const {instance: Api, asJson: resAsJson} = ApiClient;

let CACHE = {},
    FETCHING = {};

class PropertyModel {
  constructor(cfg) {
    this.cfg = cfg;
  }

  keep(properties) {
    this.cfg.properties = _.filter(this.cfg.properties, prop => {
      return _.includes(properties, prop.name);
    });
    return this;
  }

  getAll() {
    const allProperties = this.cfg.properties; // Store reference for dependent field lookup
    return allProperties.map(prop => {
      // EXISTING CODE - handle regular value mappings (UNCHANGED)
      let vmConfig = prop.valueMapping;
      prop._valueMapping = vmConfig;//copy for processing dependent fields
      if (_.includes(["Enum", "Bool", "EnumLine", "MixedEnumNumber", "OfflineState"], prop.type) && (typeof(vmConfig) === "string" || !vmConfig)) {
        if (/_?\d+\+,/.test(vmConfig) || /_?\d+\-(\d+)?,/.test(vmConfig)) {
          // EXISTING range mapping logic - UNCHANGED
          const fnBody = vmConfig.split("_")
            .reduce((acc, each) => {
              const spl = each.split(",");
              const n = spl[0].replace(/\+$|\-$/g, "");
              if (/^\d+\+$/.test(spl[0])) {
                acc.push(`if (v >= ${n}) { return "${spl[1]}"; }`);
              } else if (/^\d+\-$/.test(spl[0])) {
                acc.push(`if (v <= ${n}) { return "${spl[1]}"; }`);
              } else if (/^\d+\-\d+$/.test(spl[0])) {
                const limits = spl[0].split("-");
                acc.push(`if (v >= ${limits[0]} && v <= ${limits[1]}) { return "${spl[1]}"; }`);
              }
              return acc;
            }, []).join(" else ") + ` else { return v } `;
          prop.valueMapping = new Function("v", fnBody);
          prop.valuesDescription = vmConfig.replaceAll(",",":").replaceAll("_","\r\n");
        } else {
          // EXISTING simple mapping logic - UNCHANGED  
          const vmap = vmConfig.split("_")
            .reduce((acc, each) => {
              const spl = each.split(",");
              acc[spl[0]] = spl[1];
              return acc;
            }, {});
          const stateArray = _.map(_.keys(vmap),item=>{
            return parseInt(item);
          })
          prop.minimum = _.min(stateArray);
          prop.maximum = _.max(stateArray);
          prop.valueMapping = v => vmap[v];
          prop.valuesDescription = vmConfig.replaceAll(",",":").replaceAll("_","\r\n");
        }
      }
      
      // NEW: ONLY process dependent mapping if valueMapping is an object with dependentField
      if (typeof prop.valueMapping === 'object' && prop.valueMapping?.dependentField) {
        const dependentParsing = parseDependentValueMapping(prop, allProperties);
        if (dependentParsing) {
          // Store dependent field info
          prop.dependentField = prop.valueMapping.dependentField;
          
          // Create enhanced value mapping that accepts dependent value
          const originalMapping = prop.valueMapping;
          prop.valueMapping = createDependentValueMapping(
            dependentParsing.defaultMapping, 
            dependentParsing.dependentMappings
          );
          
          // Keep original as fallback
          prop.originalValueMapping = originalMapping;
        }
      }
      
      return prop;
    });
  }

  forPreFetch(fetchModes = ["load", "render"]) {
    return _.uniqBy(_.filter(this.cfg.properties, prop => _.includes(fetchModes, prop.preFetch)), "id");
  }

  byType(...types) {
    return _.filter(this.cfg.properties, prop => _.includes(types, prop.type));
  }

  byTypeGrouped(...types) {
    return _(this.cfg.properties)
      .filter(prop => _.includes(types, prop.type))
      .groupBy("group")
      .map((properties, group) => {
        return {
          group: group === "undefined" ? "<Ungrouped>" : group,
          properties
        };
      })
      .value();
  }

  byValueField(...valueFields) {
    return _.filter(this.cfg.properties, prop => _.includes(valueFields, prop.name));
  }

  static forModel(model, configType = "Connected") {
    const modelType = model || "NA";
    const headers = {"Accept-Encoding": "gzip"},
        id = modelType + "::" + configType,
        qs = `name=${modelType}`;

    let promise;
    if (CACHE[id]) {
      promise = Promise.resolve(CACHE[id]);
    } else if (FETCHING[id]) {
      promise = FETCHING[id];
    } else {
      promise = FETCHING[id] = Api.get(`/config/Graph_${configType}?${qs}`, {headers})
          .then(resAsJson)
          .then(response => {
            CACHE[id] = response;
            delete FETCHING[id];
            // Test Data- Commented out for now
            // const props = response.ConfigData.properties;
            // props.forEach(prop => {
            //   if (prop.name == "UPPERCOOKINGSTAGE") {
            //     prop.valueMapping = {
            //       "dependentField": "UPPERCOOKINGMODE",
            //       "mappings": {
            //         "default": "0,Preheat 1_1,Preheat 2_2,Low Band_3,High Band_4,Recovery",
            //         "3": "0,Low Broil_1,Medium Broil_2,High Broil",
            //         "11": "0,Delayed Clean_1,Initial Rise 1_2,Initial Rise 2_3,Regulation_4,Cool Down"
            //       }
            //     };
            //   }
            // });
            // console.log("Modified TestResponse: ", response);
            return response;
          })
      ;
    }

    return promise.then(response => response.ConfigData);
  }

  static getCached(model, configType = "Connected") {
    const id = model + "::" + configType;
    return (CACHE[id] || {}).ConfigData;
  }
}

// NEW: Minimal dependent value mapping parser - handles object format
function parseDependentValueMapping(prop, allProperties) {
  if (typeof prop.valueMapping !== 'object' || !prop.valueMapping?.dependentField) {
    return null; // Not a dependent field
  }
  
  try {
    const mappingConfig = prop.valueMapping;
    const dependentMappings = {};
    let defaultMapping = null;
    
    // Find the dependent field to get meaningful labels for keys
    const dependentField = allProperties?.find(p => p.name === mappingConfig.dependentField);
    const dependentValueMapping = dependentField?._valueMapping || dependentField?.valueMapping;
    
    // Create a lookup for dependent field values to labels
    const dependentLabels = {};
    if (dependentValueMapping && typeof dependentValueMapping === 'string') {
      dependentValueMapping.split('_').forEach(mapping => {
        const [value, label] = mapping.split(',');
        if (value && label) {
          dependentLabels[value] = label;
        }
      });
    }
    
    // Extract mappings from the new object format
    let valueDescription = "";
    Object.keys(mappingConfig.mappings).forEach(key => {
      const mappingValue = mappingConfig.mappings[key];
      
      if (key === 'default') {
        defaultMapping = mappingValue;
        valueDescription += `\n\rDefault:-\n\r ${mappingValue.replaceAll(",",":").replaceAll("_","\r\n")}`;
      } else {
        dependentMappings[key] = mappingValue;
        // Use meaningful label from dependent field if available
        const keyLabel = dependentLabels[key] || `Mode ${key}`;
        valueDescription += `\n\rWhen ${dependentField.displayName} is ${keyLabel}:-\n\r ${mappingValue.replaceAll(",",":").replaceAll("_","\r\n")}`;
      }
    });
    
    prop.valuesDescription = valueDescription;
    
    return { defaultMapping, dependentMappings };
  } catch (error) {
    console.warn(`Error parsing dependent mapping for ${prop.name}:`, error);
    return null;
  }
}

// NEW: Create value mapping function for dependent fields
function createDependentValueMapping(defaultMapping, dependentMappings) {
  // Create individual mapping functions
  const defaultFn = createSimpleMapping(defaultMapping);
  const mappingFns = {};
  
  Object.keys(dependentMappings).forEach(key => {
    mappingFns[key] = createSimpleMapping(dependentMappings[key]);
  });
  
  // Return enhanced function that takes dependent value
  return function(value, dependentFieldValue) {
    if (dependentFieldValue !== undefined && mappingFns[dependentFieldValue]) {
      return mappingFns[dependentFieldValue](value);
    }
    return defaultFn(value);
  };
}

// NEW: Helper to create simple mapping functions (reuses existing logic)
function createSimpleMapping(mappingStr) {
  if (!mappingStr) return v => v;
  
  const vmap = mappingStr.split("_").reduce((acc, each) => {
    const spl = each.split(",");
    acc[spl[0]] = spl[1];
    return acc;
  }, {});
  
  return v => vmap[v] || v;
}

export default PropertyModel;
